import React, { useState, useEffect } from "react";
import { Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Organization Creation Wizard
 *
 * Allows users to create a new organization with name, slug, and description.
 * Automatically generates slug from name but allows manual editing.
 *
 * Phase 1: Dual Ownership Model Component
 */

interface OrganizationCreationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (orgSlug: string) => void;
}

export const OrganizationCreationWizard: React.FC<
  OrganizationCreationWizardProps
> = ({ open, onOpenChange, onSuccess }) => {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    slug?: string;
    description?: string;
  }>({});

  const { createOrganization } = useOrganizationStore();

  // Auto-generate slug from name
  useEffect(() => {
    if (!isSlugManuallyEdited && name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric with hyphens
        .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
      setSlug(generatedSlug);
    }
  }, [name, isSlugManuallyEdited]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setName("");
      setSlug("");
      setDescription("");
      setIsSlugManuallyEdited(false);
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = "Organization name is required";
    }

    if (!slug.trim()) {
      newErrors.slug = "Organization slug is required";
    } else if (!/^[a-z0-9-]+$/.test(slug)) {
      newErrors.slug = "Slug can only contain lowercase letters, numbers, and hyphens";
    } else if (slug.length < 3) {
      newErrors.slug = "Slug must be at least 3 characters long";
    } else if (slug.length > 50) {
      newErrors.slug = "Slug must be less than 50 characters";
    }

    if (description && description.length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const newOrg = await createOrganization({
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || undefined,
      });

      toast.success("Organization created successfully", {
        description: `Created ${newOrg.name}`,
      });

      onOpenChange(false);
      onSuccess?.(newOrg.slug);
    } catch (error) {
      console.error("Failed to create organization:", error);
      toast.error("Failed to create organization", {
        description:
          error instanceof Error ? error.message : "Please try again later",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSlugChange = (value: string) => {
    setSlug(value);
    setIsSlugManuallyEdited(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-[500px] overflow-y-auto max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-card-foreground">Create organization</DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            Create a new organization to collaborate with your team and manage
            shared projects.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4 px-6">
            {/* Organization Name */}
            <div className="space-y-2">
              <label
                htmlFor="org-name"
                className="text-sm font-medium text-card-foreground"
              >
                Organization name <span className="text-destructive">*</span>
              </label>
              <input
                id="org-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Acme Corporation"
                className={cn(
                  "w-full px-3 py-2 border rounded-md bg-background text-foreground",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
                  errors.name && "border-destructive"
                )}
                disabled={isSubmitting}
                autoFocus
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Organization Slug */}
            <div className="space-y-2">
              <label
                htmlFor="org-slug"
                className="text-sm font-medium text-card-foreground"
              >
                Organization slug <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <input
                  id="org-slug"
                  type="text"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="acme-corporation"
                  className={cn(
                    "w-full px-3 py-2 border rounded-md bg-background text-foreground font-mono text-sm",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
                    errors.slug && "border-destructive"
                  )}
                  disabled={isSubmitting}
                />
              </div>
              {errors.slug ? (
                <p className="text-sm text-destructive">{errors.slug}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Used in URLs: /orgs/{slug || "your-slug"}/projects
                </p>
              )}
            </div>

            {/* Organization Description */}
            <div className="space-y-2">
              <label
                htmlFor="org-description"
                className="text-sm font-medium text-card-foreground"
              >
                Description <span className="text-muted-foreground font-normal">(Optional)</span>
              </label>
              <textarea
                id="org-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A brief description of your organization..."
                rows={3}
                className={cn(
                  "w-full px-3 py-2 border rounded-md bg-background text-foreground resize-none",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
                  errors.description && "border-destructive"
                )}
                disabled={isSubmitting}
              />
              {errors.description ? (
                <p className="text-sm text-destructive">{errors.description}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {description.length}/500 characters
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm font-medium border border-border text-card-foreground rounded-md hover:bg-muted transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || !slug.trim()}
              className={cn(
                "px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md",
                "hover:bg-primary/90 transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isSubmitting ? "Creating..." : "Create organization"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
